import i18n from "@/i18n";
/**
 * Prompt 12K-2 — Plan Cell Editor (LOCAL CORE / no live network).
 *
 * Reusable local editor for an EMPTY + APPLICABLE Current Plan cell.
 * Accepts either a canonical Day Entry code (V / X / TS / VV) OR a
 * Shift quick-time expression parsed by the existing canonical
 * `parseQuickShiftInput` helper. Emits a strictly local intent to
 * the parent for future 12K-3 live integration.
 *
 * Prompt 12K-2 §31 · §32 · §33 — MUST NOT call any HTTP client,
 * MUST NOT mutate Schedule state, MUST NOT touch ScheduleGrid or
 * WorkforceSchedule. Live network integration belongs to Prompt
 * 12K-3.
 */
import React, {
    useEffect,
    useRef,
    useState,
} from "react";
import { toast } from "sonner";

import { VVContextNote } from "@/components/workforce/VVContextNote";
import { Input } from "@/components/ui/input";
import {
    parseQuickShiftInput,
    normalizeShiftIntervalInput,
} from "@/components/workforce/workforceHelpers";

// Prompt 12K-2 §5 — canonical Day Entry codes. Opaque strings; the
// four values below are the only acceptable strings, treated as
// canonical identifiers only (no domain semantics interpreted).
export const DAY_ENTRY_CODES = ["V", "X", "TS", "VV"];

// Prompt 12K-2 §8 — exact combined guidance for an invalid empty
// cell input. Do NOT change punctuation or capitalization.
export const PLAN_CELL_INVALID_GUIDANCE =
    "Use a time like 8-16 or a day code: V, X, TS, VV.";

// Prompt 12K-2 §6 — pure Day Entry normalization primitive. Trim
// then uppercase; only V / X / TS / VV are accepted. No aliases,
// no fuzzy matching.
export function normalizeDayEntryCode(raw) {
    if (typeof raw !== "string") return null;
    const trimmed = raw.trim();
    if (trimmed === "") return null;
    const upper = trimmed.toUpperCase();
    if (upper === "V" || upper === "X" || upper === "TS" || upper === "VV") {
        return upper;
    }
    return null;
}

// Prompt 12K-2 §7 — combined empty-cell parse order: exact Day
// Entry code FIRST, existing Shift quick parser SECOND.
// Prompt 12K-2 §7 · §31 — no second Shift parser is introduced.
export function parseEmptyCellInput(raw, workDate) {
    const dayCode = normalizeDayEntryCode(raw);
    if (dayCode !== null) {
        return { kind: "day-entry", code: dayCode };
    }
    // Prompt 18B2-P4 §6 · §7 · §10 — friendly manager time syntax
    // ("20.20-22.30", "2020-2230", etc.) is pre-normalized to the
    // canonical `HH:MM-HH:MM` shape that the existing strict Shift
    // parser already understands. When the input is not a two-token
    // interval, fall back to the raw string so `parseQuickShiftInput`
    // remains the sole authority on validity.
    const normalized = normalizeShiftIntervalInput(raw) || raw;
    const parsed = parseQuickShiftInput(normalized, workDate);
    if (parsed !== null) {
        return { kind: "shift", parsed };
    }
    return null;
}

// Prompt 12K-2 §9 — eligible cells only. Empty + applicable.
function _isEligible(cell) {
    if (!cell || cell.applicable !== true) return false;
    if (!Array.isArray(cell.shifts) || cell.shifts.length !== 0) return false;
    if (!Array.isArray(cell.day_entries)) return false;
    if (cell.day_entries.length !== 0) return false;
    return true;
}

export function PlanCellEditor({
    cell,
    onIntent,
    isPending,
    children,
    cellKey,
    cancelEditVersion,
    vvContext,
}) {
    const eligible = _isEligible(cell);

    const [isEditing, setIsEditing] = useState(false);
    const [text, setText] = useState("");

    const inputRef = useRef(null);
    // Prompt 12K-2 §15 — mirror the existing zero-Shift blur
    // semantics: deferred by one browser task turn so a coincident
    // Enter / Tab / Shift+Tab / Escape wins.
    const blurTimerRef = useRef(null);
    const committedRef = useRef(false);
    const cancelSeenRef = useRef(cancelEditVersion);

    const cancelBlurTimer = () => {
        if (blurTimerRef.current !== null) {
            clearTimeout(blurTimerRef.current);
            blurTimerRef.current = null;
        }
    };

    useEffect(() => {
        return () => {
            cancelBlurTimer();
        };
    }, []);

    // Prompt 12K-2 §14 — preserve existing Schedule quick-edit
    // cancellation style via an opaque parent-controlled bump.
    useEffect(() => {
        if (cancelSeenRef.current === cancelEditVersion) return;
        cancelSeenRef.current = cancelEditVersion;
        cancelBlurTimer();
        committedRef.current = true;
        setIsEditing(false);
        setText("");
    }, [cancelEditVersion]);

    useEffect(() => {
        if (!isEditing) return;
        const el = inputRef.current;
        if (!el) return;
        el.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEditing]);

    const openEditor = () => {
        committedRef.current = false;
        cancelBlurTimer();
        setText("");
        setIsEditing(true);
    };

    const emitIntent = (parseResult, navigate, source) => {
        // Prompt 12K-2 §27 — do not emit additional mutation intents
        // while a previous mutation is still pending.
        if (isPending) return;
        if (committedRef.current) return;
        committedRef.current = true;
        cancelBlurTimer();
        if (typeof onIntent === "function") {
            if (parseResult.kind === "day-entry") {
                // Prompt 12K-2 §10 · §11 — frozen Day Entry create
                // intent shape. NO restaurant_id / period_id /
                // employee_id / date / revision / URL / sentinel here.
                onIntent({
                    mode: "create-day-entry",
                    code: parseResult.code,
                    navigate,
                    source,
                });
            } else {
                // Prompt 12K-2 §10 · §11 — frozen Shift create intent
                // shape reusing the canonical parser result.
                onIntent({
                    mode: "create-shift",
                    parsed: parseResult.parsed,
                    navigate,
                    source,
                });
            }
        }
        setIsEditing(false);
        setText("");
    };

    const showGuidance = () => {
        toast.error(i18n.t("wf.PlanCellEditor.PLAN_CELL_INVALID_GUIDANCE", PLAN_CELL_INVALID_GUIDANCE));
    };

    // Prompt 12K-2 §11 · §12 · §13 · §14 — non-editing wrapper key
    // handling. Only Enter opens the editor; Delete / Backspace on
    // an empty cell are no-ops here (K2 owns editor core only).
    const handleWrapperKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            openEditor();
        }
    };

    const handleWrapperClick = () => {
        openEditor();
    };

    // Prompt 12K-2 §11 · §12 · §13 · §14 — inline input key handling.
    const handleInputKeyDown = (e) => {
        if (e.key === "Escape") {
            // Prompt 12K-2 §14 — cancel local edit, clear validation
            // state, emit no mutation intent.
            e.preventDefault();
            cancelBlurTimer();
            committedRef.current = true;
            setIsEditing(false);
            setText("");
            return;
        }

        if (e.key === "Enter") {
            e.preventDefault();
            const raw = typeof text === "string" ? text : "";
            const trimmed = raw.trim();
            if (trimmed === "") {
                // Blank Enter cancels silently — mirrors existing
                // zero-Shift editor behavior.
                cancelBlurTimer();
                committedRef.current = true;
                setIsEditing(false);
                setText("");
                return;
            }
            const parseResult = parseEmptyCellInput(raw, cell.date);
            if (parseResult === null) {
                // Prompt 12K-2 §11 · §8 — invalid: guidance, no intent.
                cancelBlurTimer();
                showGuidance();
                return;
            }
            // Prompt 12K-2 §11 — Enter with valid input: navigate=null.
            emitIntent(parseResult, null, "plan-cell-editor");
            return;
        }

        if (e.key === "Tab") {
            e.preventDefault();
            const navigate = e.shiftKey ? "previous" : "next";
            const raw = typeof text === "string" ? text : "";
            const trimmed = raw.trim();
            if (trimmed === "") {
                cancelBlurTimer();
                committedRef.current = true;
                setIsEditing(false);
                setText("");
                return;
            }
            const parseResult = parseEmptyCellInput(raw, cell.date);
            if (parseResult === null) {
                cancelBlurTimer();
                showGuidance();
                return;
            }
            // Prompt 12K-2 §12 · §13 — Tab / Shift+Tab: navigate
            // "next" / "previous". No permanent focus movement here.
            emitIntent(parseResult, navigate, "plan-cell-editor");
        }
    };

    // Prompt 12K-2 §15 — preserve existing zero-Shift editor blur
    // semantics EXACTLY. Deferred by one browser task turn so a
    // coincident Enter / Tab / Escape wins.
    const handleInputBlur = () => {
        cancelBlurTimer();
        const capturedText = text;
        const capturedDate = cell.date;
        blurTimerRef.current = setTimeout(() => {
            blurTimerRef.current = null;
            if (committedRef.current) return;
            const trimmed =
                typeof capturedText === "string" ? capturedText.trim() : "";
            if (trimmed === "") {
                committedRef.current = true;
                setIsEditing(false);
                setText("");
                return;
            }
            const parseResult = parseEmptyCellInput(
                capturedText, capturedDate,
            );
            if (parseResult === null) {
                showGuidance();
                committedRef.current = true;
                setIsEditing(false);
                setText("");
                return;
            }
            emitIntent(parseResult, null, "plan-cell-editor");
        }, 0);
    };

    if (!eligible) {
        return <>{children}</>;
    }

    if (isEditing) {
        // TES-B2C1B2 — contextual VV state only while VV is entered.
        const showVV = vvContext && normalizeDayEntryCode(text) === "VV";
        return (
            <>
                <Input
                    ref={inputRef}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={handleInputKeyDown}
                    onBlur={handleInputBlur}
                    data-testid="plan-cell-editor-input"
                    aria-label={i18n.t("wf.planCellEditor.planCellQuickInput", "Plan cell quick input")}
                />
                {showVV ? (
                    <VVContextNote ctx={vvContext}
                        testId="plan-cell-editor-vv-context" />
                ) : null}
            </>
        );
    }

    // Prompt PREVIEW-FIX-02-P1 — wrapper is presented as a block
    // surface so the entire padded cell area is a valid click target.
    // Preserves the canonical <span> tag / testid / a11y attributes
    // required by Workforce12K2 + Workforce12K3.
    return (
        <span
            tabIndex={0}
            role="button"
            onClick={handleWrapperClick}
            onKeyDown={handleWrapperKeyDown}
            data-testid="plan-cell-editor-wrapper"
            data-workforce-plan-cell-key={cellKey}
            style={{
                display: "block",
                minHeight: "24px",
                width: "100%",
                cursor: "text",
            }}
        >
            {children}
        </span>
    );
}

export default PlanCellEditor;
